import { Form } from "antd"
import { Alert, Checkbox, Legend, RadioButtons, SelectInput, TextInput } from "components/common"
import { t } from "i18next"
import { interestOptions } from "../utils"
import { useLazyGetClientNotInterestedReasonsQuery } from "features/leads/api"
import { useMemo, useState } from "react"
import { mapKeysToValues } from "utils"
import { IconText } from "components/custom"
import theme from "utils/themeVars"
import getPrivilege from "utils/privileges/privileges"
import { useSelector } from "react-redux"

const AuditReviewForm = ({ form, rules, concerns, isFetchingConcerns, lead, isFlaggedInAuditReview }) => {
  const userPrivileges = useSelector(state => state.app.privileges)

  const clientInterest = Form.useWatch("clientInterested", form)
  const clientinterestReasonId = Form.useWatch("clientNotInterestedReasonId", form)
  const clientConcernIds = Form.useWatch("areasOfImprovementsIds", form)
  const addAuditConcernFlagCheck = Form.useWatch("addAuditConcernFlag", form)

  const [getClientNotInterestedreasons, { data: reasons, isFetching: isFetchingReasons }] =
    useLazyGetClientNotInterestedReasonsQuery()

  const reasonsKeyMap = useMemo(() => (reasons ? mapKeysToValues(reasons) : {}), [reasons])
  const concernsKeyMap = useMemo(() => (concerns ? mapKeysToValues(concerns, "key", "value") : {}), [concerns])
  const [auditCriteriaMet, setAuditCriteriaMet] = useState(false)

  const getReviewMessage = () => {
    return !auditCriteriaMet
      ? t(
          "Based on your review selections, this lead no longer meets the audit flag criteria and will be removed from Audit Concerns after submission."
        )
      : t(
          "This lead already has an audit concern flag. Adding this review will not remove it from the Audit Concerns list."
        )
  }

  const onConcernChange = (value, interest) => {
    if (!value?.length) {
      setAuditCriteriaMet(false)
      return
    }
    const clientInterestOption = interest ?? clientInterest
    if (clientInterestOption) {
      const blockedKeys = [concernsKeyMap?.["no_call_recordings_found"], concernsKeyMap?.["no_client_voice"]]
      const hasBlockedKey = blockedKeys.some(key => value.includes(key))
      if (hasBlockedKey) {
        setAuditCriteriaMet(false)
        form.setFieldsValue({ addAuditConcernFlag: false })
      } else {
        setAuditCriteriaMet(true)
        form.setFieldsValue({ addAuditConcernFlag: true })
      }
    } else {
      setAuditCriteriaMet(false)
      form.setFieldsValue({ addAuditConcernFlag: false })
    }
  }

  return (
    <>
      <Legend title={t("Progression")} />
      <Form.Item name="clientInterested" label={t("Client Interest")} required rules={rules}>
        <RadioButtons
          options={interestOptions()}
          getValue={e => e.value}
          onChange={e => {
            !e?.target?.value && getClientNotInterestedreasons(true)
            onConcernChange(clientConcernIds, e?.target?.value)
          }}
        />
      </Form.Item>
      {!clientInterest && (
        <>
          <Form.Item name={"clientNotInterestedReasonId"} label={t("Reason")} rules={rules} required>
            <SelectInput
              placeholder={t("Select reason")}
              options={reasons}
              getOptionValue={e => e?.id}
              inputLoading={isFetchingReasons}
              onChange={(value, option) => {
                form.setFieldsValue({ clientNotInterestedReasonId: value })
              }}
            />
          </Form.Item>
          <Form.Item
            name="clientNotInterestedReasonValue"
            label={t("Comment")}
            required={clientinterestReasonId ? reasonsKeyMap?.["other"] === clientinterestReasonId : false}
            rules={clientinterestReasonId ? reasonsKeyMap?.["other"] === clientinterestReasonId && rules : []}
            extra={
              <IconText
                icon="MdInfoOutline"
                iconProps={{ color: theme["gray800"], size: "1em" }}
                textType="secondary"
                title={t("Maximum 40 characters")}
              />
            }
          >
            <TextInput placeholder={t("Enter comment")} maxLength={40} />
          </Form.Item>
        </>
      )}
      <Legend title={t("Areas of Concern")} />
      <Form.Item name={"areasOfImprovementsIds"} label={t("Concerns")} rules={rules}>
        <SelectInput
          placeholder={t("Select Concerns")}
          options={concerns}
          inputLoading={isFetchingConcerns}
          mode="multiple"
          onChange={value => onConcernChange(value)}
        />
      </Form.Item>
      {clientConcernIds?.includes(concernsKeyMap?.["other"]) && (
        <Form.Item
          name="otherImprovement"
          label={t("Others")}
          required
          rules={rules}
          extra={
            <IconText
              icon="MdInfoOutline"
              iconProps={{ color: theme["gray800"], size: "1em" }}
              textType="secondary"
              title={t("Maximum 25 characters")}
            />
          }
        >
          <TextInput placeholder={t("Enter Others")} maxLength={25} />
        </Form.Item>
      )}
      <Legend title={t("Overall Feedback")} />
      <Form.Item
        name="feedback"
        label={t("Feedback")}
        rules={rules}
        required
        extra={
          <IconText
            icon="MdInfoOutline"
            iconProps={{ color: theme["gray800"], size: "1em" }}
            textType="secondary"
            title={t("Maximum 500 characters")}
          />
        }
      >
        <TextInput placeholder={t("Enter feedback")} maxLength={500} lineCount={4} style={{ resize: "none" }} />
      </Form.Item>
      <Form.Item name="addAuditConcernFlag" required rules={rules}>
        <Checkbox
          disabled={auditCriteriaMet}
          checked={addAuditConcernFlagCheck}
          onChange={e => form.setFieldValue("addAuditConcernFlag", e?.target?.checked)}
        >
          {lead?.isInAuditConcern ? t("Retain in Audit Concern") : t("Add to Audit Concern")}
        </Checkbox>
      </Form.Item>
      {lead?.isInAuditConcern && <Alert type="error" message={getReviewMessage()} className="mbe-8" />}
    </>
  )
}

export default AuditReviewForm
